import { Checkbox, DatePicker, message, Select, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { serverFetch } from "@server";
import "../records/records.css";
import RecordsHeader from "../records/records-header";
import RecordsLine from "../records/records-line";
import dayjs from "dayjs";
import Record from "../records/record";
import RecordsItem from "../records/records-item";
import { strDate2Server } from "@extensions/convert";
import { refreshButton } from "@extensions/design";
import { tasks } from "@extensions/enums";


const hourStart = 7;
const hourFinish = 23;

/** Планирование */
const TaskPlans = () => {
  const [users, setUsers] = useState([]);
  const [date, setDate] = useState(dayjs());
  const [selectedTime, setSelectedTime] = useState(); // Выбранное время для добавления новой записи
  const [selectedItem, setSelectedItem] = useState(); // Выбранная запись
  const [records, setRecords] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [userColWidth, setUserColWidth] = useState(() => {
    // Загружаем сохраненную ширину из localStorage
    const saved = localStorage.getItem('records-userColWidth');
    return saved ? parseInt(saved) : 200;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [taskId, setTaskId] = useState(1);

  const getUsers = () => {
    serverFetch("plan/dep")
      .then(data => setUsers([{ id: null, name: "Не назначен", active: true }, ...data.filter(i => i.active)]))
      .catch(e => message.error(e.userMessage));
  };

  const getRecords = () => {
    serverFetch(`plan/doc/planned?dateBeg=${strDate2Server(date)}&dateEnd=${strDate2Server(date)}&taskId=${taskId}`)
      .then(data => {
        const newRecords = data.map(i => ({ ...i,
          start: i.startDt ? dayjs(i.startDt) : null,
          finish: i.finishDt ? dayjs(i.finishDt) : null
        }));
        setRecords(newRecords);
      })
      .catch(e => message.error(e.userMessage));
    getUsers();
  };

  useEffect(() => {
    getUsers();
  }, []);


  useEffect(getRecords, [date, taskId]);

  // Ширина столбца времени
  const timeColWidth = 100;
  // Высота шапки
  const headerRowHeight = 50;
  // Высота строки часа
  const hourRowHeight = 50;

  // Функция для сохранения ширины колонки в localStorage
  const saveUserColWidth = (width) => {
    localStorage.setItem('records-userColWidth', width.toString());
  };

  // Обработчики ресайза колонки
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    document.body.classList.add('resizing');
    const startX = e.clientX;
    const startWidth = userColWidth;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(100, Math.min(400, startWidth + deltaX));
      setUserColWidth(newWidth);
      saveUserColWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.classList.remove('resizing');
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const isToday = dayjs(date).isSame(dayjs(), 'day');

  const grafs = [{ startMin: hourStart * 60, finishMin: hourFinish*60 }];

  const divLines = Array.from({ length: hourFinish - hourStart ?? 0}, (_, hour) =>
    <RecordsLine 
      key={hourStart + hour} 
      hour={hourStart + hour} 
      users={users} 
      height={hourRowHeight} 
      timeWidth={timeColWidth} 
      userWidth={userColWidth} 
      isToday={isToday}
      grafs={grafs}
      onSelectTime={value => {
        console.log(value);
        setSelectedTime({datePlan: date, docDate: dayjs(), ...value, taskId });
      }}
    />)  

  const onCloseEdit = (refresh) => {
    setSelectedItem(null);
    setSelectedTime(null);
    refresh && getRecords();
  }

  const filteredItems = records.filter(i => showDeleted || !i.deleted);

  // Обработчики drag and drop для контейнера записей
  const handleContainerDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleContainerDragLeave = (e) => {
    // Проверяем, что мы действительно покинули контейнер, а не перешли к дочернему элементу
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleContainerDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
      
      // Определяем новую позицию на основе координат drop относительно контейнера
      const rect = e.currentTarget.getBoundingClientRect();
      const dropX = e.clientX - rect.left;
      const dropY = e.clientY - rect.top;
      
      // Вычисляем новый столбец (сотрудник/зал)
      const newColumnIndex = Math.floor((dropX - timeColWidth) / userColWidth);
      const newUser = users[newColumnIndex];
      
      // Вычисляем новое время с шагом 15 минут
      const relativeY = dropY - headerRowHeight;
      const minutesFromStart = (relativeY / hourRowHeight) * 60;
      const roundedMinutes = Math.round(minutesFromStart / 15) * 15;
      const newStart = dayjs().startOf('day').add((hourStart ?? 0) * 60 + roundedMinutes, 'minute');
      
      // Вычисляем новое время окончания (сохраняем длительность)
      const duration = (dragData.finish && dragData.start) ? dayjs(dragData.finish, 'HH:mm').diff(dayjs(dragData.start, 'HH:mm'), 'minute') : 60;
      const newFinish = newStart.add(duration, 'minute');

      // Обновляем запись
      updateRecordOnServer(dragData.id, {
        newUserId: newUser?.id,
        newStart: newStart,
        newFinish: newFinish
      });
    } catch (error) {
      console.error('Error handling container drop:', error);
    }
  };

  // Функция для обновления записи на сервере
  const updateRecordOnServer = async (recordId, newData) => {
    try {
      // Находим запись в локальном состоянии
      const recordIndex = records.findIndex(record => record.id === recordId);
      if (recordIndex === -1) {
        message.error('Запись не найдена');
        return;
      }
      
      // Создаем обновленную запись
      const updatedRecord = {
        ...records[recordIndex],
        userId: newData.newUserId,
        startDt: newData.newStart.format('YYYY-MM-DDTHH:mm:ss'),
        finishDt: newData.newFinish.format('YYYY-MM-DDTHH:mm:ss')
      };

      // Обновляем локальное состояние
      setRecords(prevRecords => {
        const newRecords = [...prevRecords];
        newRecords[recordIndex] = updatedRecord;
        return newRecords;
      });

      // Подготавливаем полную обновленную запись для отправки на сервер
      const updateData = {
        ...updatedRecord,
        docDate: strDate2Server(updatedRecord.docDate),
        // startDt: newData.newStart.format('HH:mm:ss'),
        // startDt: newData.newFinish.format('HH:mm:ss')
      };
      
      // Отправляем запрос на сервер
      await serverFetch(`plan/doc`, { 
        method: 'PUT', 
        bodyData: updateData 
      });
      getRecords();
      message.success('Запись успешно перемещена');
    } catch (error) {
      // В случае ошибки откатываем изменения
      getRecords();
      message.error(error.userMessage || 'Ошибка при перемещении записи');
    }
  };

  return <>
    <div className="page-cloud-row">
      <div><div>Задача</div><Select value={taskId} onChange={e => setTaskId(e)} options={tasks} style={{width: 200}}/></div>
      <div><div>Дата</div><DatePicker value={date} onChange={e => setDate(e)} format="DD.MM.YYYY" allowClear={false} style={{maxWidth: 120}}/></div>
      <Tooltip title="Показать отмененные"><Checkbox checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)}>Отмененные</Checkbox></Tooltip>
      {refreshButton(getRecords)}
    </div>
    <div className={`records ${isDragOver ? 'drag-over' : ''}`} onDragOver={handleContainerDragOver} onDragLeave={handleContainerDragLeave} onDrop={handleContainerDrop}>
      <RecordsHeader 
        users={users} 
        height={headerRowHeight} 
        timeWidth={timeColWidth} 
        userWidth={userColWidth} 
        records={filteredItems} 
        onResize={handleMouseDown}
      />
      <div className="records-lines">{divLines}</div>
      {(() => {
        // Группируем записи по пользователям для обработки null start
        const itemsByUser = {};
        filteredItems.forEach(item => {
          const userId = item.userId ?? null;
          const userKey = userId === null ? 'null' : userId.toString();
          if (!itemsByUser[userKey]) {
            itemsByUser[userKey] = { userId, withStart: [], withoutStart: [] };
          }
          if (item.start) {
            itemsByUser[userKey].withStart.push(item);
          } else {
            itemsByUser[userKey].withoutStart.push(item);
          }
        });

        // Сортируем записи с null start по id для стабильного порядка
        Object.keys(itemsByUser).forEach(userKey => {
          itemsByUser[userKey].withoutStart.sort((a, b) => (a.id || 0) - (b.id || 0));
        });

        // Создаем массив всех записей с вычисленными позициями
        const itemsWithPositions = [];
        
        Object.keys(itemsByUser).forEach(userKey => {
          const userItems = itemsByUser[userKey];
          const userId = userItems.userId;
          const idx = users.indexOf(users.find(i => i.id === userId));
          
          // Обрабатываем записи с null start - располагаем друг под другом в начале дня
          let nullStartOffset = 0;
          userItems.withoutStart.forEach(item => {
            const left = timeColWidth + (idx + 0.05)*userColWidth;
            const top = headerRowHeight + nullStartOffset * hourRowHeight;
            const height = hourRowHeight; // 1 час по умолчанию
            itemsWithPositions.push({ item, left, top, width: userColWidth*0.9, height });
            nullStartOffset += 1; // Следующая запись ниже на 1 час
          });

          // Обрабатываем записи с start
          userItems.withStart.forEach(item => {
            const left = timeColWidth + (idx + 0.05)*userColWidth;
            const top = headerRowHeight + (item.start.diff(item.start.startOf('day'), 'minute') - (hourStart ?? 0)*60)/60*hourRowHeight;
            const height = item.finish && item.start 
              ? item.finish.diff(item.start, "minute")/60*hourRowHeight 
              : hourRowHeight;
            itemsWithPositions.push({ item, left, top, width: userColWidth*0.9, height });
          });
        });

        return itemsWithPositions.map(({ item, left, top, width, height }) => (
          <RecordsItem 
            item={item} 
            left={left} 
            top={top} 
            width={width} 
            height={height} 
            onClick={setSelectedItem} 
            key={item.id}
            timeColWidth={timeColWidth}
            userColWidth={userColWidth}
            headerRowHeight={headerRowHeight}
            hourRowHeight={hourRowHeight}
            todayUsers={users}
            updateRecordOnServer={updateRecordOnServer}
            hourStart={hourStart}
          />
        ));
      })()}
    </div>
    {selectedTime && <Record defaultItem={selectedTime}  onClose={onCloseEdit} users={users}/>}
    {selectedItem && <Record id={selectedItem} onClose={onCloseEdit} users={users}/>}
  </>
}

export default TaskPlans;
